/**
 * Arcℏimedian DSaaS — Auth Context
 * Provides user state, login/logout, active workspace, across the app.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { tokenStorage } from '../api/client';
import { authService } from '../api/services';
import { queryKeys } from '../api/hooks';
import type { User, LoginRequest, RegisterRequest, TokenResponse, Workspace } from '../api/types';
import i18n from '../i18n';

function applyUserLanguage(user: User) {
  const lang = (user.preferences?.language as string) || null;
  if (lang && (lang === 'en' || lang === 'pt')) {
    i18n.changeLanguage(lang);
    localStorage.setItem('archimedian_lang', lang);
  }
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  activeWorkspace: Workspace | null;
}

interface AuthContextValue extends AuthState {
  login: (data: LoginRequest) => Promise<User>;
  register: (data: RegisterRequest) => Promise<User>;
  logout: () => void;
  setActiveWorkspace: (workspace: Workspace | null) => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const WORKSPACE_KEY = 'archimedian_active_workspace';

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    activeWorkspace: null,
  });

  // Restore workspace from localStorage
  const restoreWorkspace = (): Workspace | null => {
    try {
      const stored = localStorage.getItem(WORKSPACE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };

  // Load user on mount if token exists
  useEffect(() => {
    const loadUser = async () => {
      const token = tokenStorage.getAccess();
      if (!token) {
        setState((s) => ({ ...s, isLoading: false }));
        return;
      }

      try {
        const { data: user } = await authService.me();
        applyUserLanguage(user);
        setState({
          user,
          isAuthenticated: true,
          isLoading: false,
          activeWorkspace: restoreWorkspace(),
        });
      } catch {
        tokenStorage.clear();
        setState({ user: null, isAuthenticated: false, isLoading: false, activeWorkspace: null });
      }
    };

    loadUser();
  }, []);

  // Listen for forced logouts (from interceptor)
  useEffect(() => {
    const handleLogout = () => {
      setState({ user: null, isAuthenticated: false, isLoading: false, activeWorkspace: null });
      queryClient.clear();
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, [queryClient]);

  const login = useCallback(async (data: LoginRequest): Promise<User> => {
    const { data: tokens } = await authService.login(data);
    tokenStorage.set(tokens);

    const workspace = tokens.workspace ?? restoreWorkspace();
    if (tokens.workspace) {
      localStorage.setItem(WORKSPACE_KEY, JSON.stringify(tokens.workspace));
    }

    const { data: user } = await authService.me();
    applyUserLanguage(user);
    setState({ user, isAuthenticated: true, isLoading: false, activeWorkspace: workspace });
    queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });
    return user;
  }, [queryClient]);

  const register = useCallback(async (data: RegisterRequest) => {
    const { data: tokens } = await authService.register(data);
    tokenStorage.set(tokens);

    if (tokens.workspace) {
      localStorage.setItem(WORKSPACE_KEY, JSON.stringify(tokens.workspace));
    }

    const { data: user } = await authService.me();
    setState({
      user,
      isAuthenticated: true,
      isLoading: false,
      activeWorkspace: tokens.workspace ?? null,
    });
    return user;
  }, []);

  const logout = useCallback(() => {
    tokenStorage.clear();
    localStorage.removeItem(WORKSPACE_KEY);
    setState({ user: null, isAuthenticated: false, isLoading: false, activeWorkspace: null });
    queryClient.clear();
  }, [queryClient]);

  const setActiveWorkspace = useCallback((workspace: Workspace | null) => {
    setState((s) => ({ ...s, activeWorkspace: workspace }));
    if (workspace) {
      localStorage.setItem(WORKSPACE_KEY, JSON.stringify(workspace));
    } else {
      localStorage.removeItem(WORKSPACE_KEY);
    }
    // Invalidate workspace-scoped queries
    queryClient.invalidateQueries();
  }, [queryClient]);

  const refreshUser = useCallback(async () => {
    try {
      const { data: user } = await authService.me();
      setState((s) => ({ ...s, user }));
    } catch {
      // ignore
    }
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, register, logout, setActiveWorkspace, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// ── Route Guard Component ──
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    window.location.href = '/login';
    return null;
  }

  return <>{children}</>;
}

// ── Role Guard ──
export function RequireRole({ children, roles }: { children: ReactNode; roles: string[] }) {
  const { user } = useAuth();

  if (!user || !roles.includes(user.role)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">You don't have permission to access this page.</p>
      </div>
    );
  }

  return <>{children}</>;
}
